import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type ExpensePaymentMethod = 'CASH' | 'BANK_TRANSFER' | 'CREDIT_CARD';
export type ExpensePaymentStatus = 'PAID' | 'CANCELLED';

export type ExpenseLedgerCategory = 'EXPENSE' | 'COMMISSION';

export interface ExpenseLedgerItem {
  id: string;
  transactionNo: string;
  paymentDate: string;
  transferredAt: string | null;
  bookingNo: string;
  customerName: string;
  tourCode: string;
  tourName: string;
  country: string;
  category: ExpenseLedgerCategory;
  description: string;
  requestedByName: string;
  approvedByName: string;
  approvedAt: string;
  paymentMethod: ExpensePaymentMethod;
  bankName: string | null;
  accountNumber: string | null;
  status: ExpensePaymentStatus;
  amount: number;
  withholdingTaxAmount: number;
  documentUrl: string | null;
  receiptPath: string | null;
}

export interface ExpenseLedgerFilters {
  dateFrom: string;
  dateTo: string;
  category: 'ALL' | ExpenseLedgerCategory;
  status: 'ALL' | ExpensePaymentStatus;
}

@Injectable({ providedIn: 'root' })
export class ExpenseLedgerService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/accounting/expense`;

  async getItems(filters: ExpenseLedgerFilters): Promise<ExpenseLedgerItem[]> {
    let params = new HttpParams()
      .set('category', filters.category)
      .set('status', filters.status);
    if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params = params.set('dateTo', filters.dateTo);

    const response = await firstValueFrom(
      this.http.get<{ items: ExpenseLedgerItem[] }>(this.apiUrl, {
        params,
        withCredentials: true,
      }),
    );
    return response.items;
  }

  async updateSlip(
    id: string,
    input: {
      receiptUrl: string;
      transferDate: string;
      transferTime: string;
      withholdingTaxAmount: number;
    },
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.apiUrl}/${id}/slip`,
        input,
        { withCredentials: true },
      ),
    );
  }

  resolveFileUrl(fileUrl: string): string {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (/^https?:\/\//i.test(environment.apiUrl)) {
      return `${new URL(environment.apiUrl).origin}${fileUrl}`;
    }
    return fileUrl;
  }
}
